import React, { FC, MouseEvent, useState } from "react";
import ReactFlow, { Background } from "react-flow-renderer";
import { useStore, connect } from "src/store/connect";
import { Button, Popover, Snackbar } from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditBlock from "src/components/EditBlock";
import { isNode } from "react-flow-renderer";

import SimpleBlock from "src/components/SimpleBlock";
import RootBlock from "src/components/RootBlock";
import MultipleOptionsBlock from "src/components/MultipleOptionsBlock";
import PlaceholderBlock from "src/components/PlaceholderBlock";
import FinishBlock from "src/components/FinishBlock";
import BackgroundCheckBlock from "src/components/BackgroundCheckBlock";
import DocumentValidationBlock from "src/components/DocumentValidationBlock";
import EmailValidationBlock from "src/components/EmailValidationBlock";
import PhoneValidationBlock from "src/components/PhoneValidationBlock";
import mixpanel from "mixpanel-browser";

const NodeTypes: Record<BlockType, FC<any>> = {
  root: RootBlock,
  placeholder: PlaceholderBlock,
  simple: SimpleBlock,
  multiple: MultipleOptionsBlock,
  recursive: SimpleBlock, // TODO: replace with recursive component
  finish: FinishBlock,
  background_check: BackgroundCheckBlock,
  document_validation_segment: DocumentValidationBlock,
  email_validation_segment: EmailValidationBlock,
  phone_validation_segment: PhoneValidationBlock,
};

interface CanvasProps {}

const Canvas: FC<CanvasProps> = () => {
  const classes = useStyles();
  const state = useStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [targetBlock, setTargetBlock] = useState<ElementReactFlow | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  const onRemoveBlock = () => {
    if (!targetBlock) {
      return;
    }

    state.removeBlock(targetBlock.id);
    setAnchorEl(null);
    setSnackbarOpen(true);

    mixpanel.track("block_deleted", { block_type: targetBlock.type });
  };

  const onElementClick = (event: MouseEvent, element: ElementReactFlow) => {
    if (!isNode(element)) {
      return;
    }
    if (
      element.type !== "multiple" &&
      element.type !== "simple" &&
      element.type !== "finish" &&
      element.type !== "background_check" &&
      element.type !== "document_validation_segment" &&
      element.type !== "email_validation_segment" &&
      element.type !== "phone_validation_segment"
    ) {
      return;
    }

    setTargetBlock(element);
    setAnchorEl(event.target as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseSnackbar = (
    event: React.SyntheticEvent<any, Event>,
    reason: String
  ): void => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarOpen(false);
  };

  const handleUndoDeletion = (): void => {
    setSnackbarOpen(false);
    state.restoreFlow();
    mixpanel.track("block_deletion_undid");
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    // TODO: this to avoid vertical scroll cause arbitrary canvas height?
    // const onLoad = (reactFlowInstance) => reactFlowInstance.fitView({ padding: 0.2 });
    <div style={{ height: "75vh" }}>
      {open && <div className={classes.openModalContainer}></div>}
      <ReactFlow
        elements={state.flowElements}
        nodeTypes={NodeTypes}
        onElementClick={onElementClick}
      >
        <Background gap={30} size={2} color="#D6DCE4" />
      </ReactFlow>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <EditBlock
          onRemoveBlock={onRemoveBlock}
          targetBlock={targetBlock}
          onClose={handleClose}
        />
      </Popover>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message="Bloque eliminado"
        ContentProps={{
          className: classes.snackbarContainer,
        }}
        action={
          <React.Fragment>
            <Button
              color="primary"
              size="small"
              onClick={() => handleUndoDeletion()}
            >
              DESHACER
            </Button>
          </React.Fragment>
        }
      />
    </div>
  );
};

export default connect(Canvas);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: "25px 32px 15px 32px",
    backgroundColor: "#655a50",
    display: "flex",
    justifyContent: "flex-end",
  },
  openModalContainer: {
    backgroundColor: "rgba(38, 38, 38, 0.69)",
    position: "fixed",
    padding: 0,
    margin: 0,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 80,
  },
  snackbarContainer: {
    backgroundColor: "whitesmoke",
    color: "#1d262c",
  },
}));
